import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import OperationsOwnerPerformanceReportApi from "../../../../infrastructure/api/operationsOwnerPerformanceReport";
import useGetPageInfo from "../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../components/shared/table";
import {
  toJalaliFullDate,
  toLocalDateInputValue,
} from "../../../middleware/middleware";

const OPERATION_MODULES = [
  { value: "all", label: "همه" },
  { value: "mehrsan", label: "مهرسان" },
  { value: "lending", label: "وام" },
];

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  const format = (d) => toLocalDateInputValue(d);
  return { fromDate: format(from), toDate: format(to) };
};

const initialFilters = {
  operationModule: "all",
  assignedToUserId: "",
  ...getDefaultDateRange(),
};

const normalizeRow = (raw) => ({
  assignedToUserId: raw.assignedToUserId ?? raw.AssignedToUserId ?? null,
  assignedToFullName:
    raw.assignedToFullName ?? raw.AssignedToFullName ?? "—",
  totalProcesses: raw.totalProcesses ?? raw.TotalProcesses ?? 0,
  openProcesses: raw.openProcesses ?? raw.OpenProcesses ?? 0,
  completedProcesses: raw.completedProcesses ?? raw.CompletedProcesses ?? 0,
  overdueProcesses: raw.overdueProcesses ?? raw.OverdueProcesses ?? 0,
  attentionProcesses: raw.attentionProcesses ?? raw.AttentionProcesses ?? 0,
  normalProcesses: raw.normalProcesses ?? raw.NormalProcesses ?? 0,
  totalLogs: raw.totalLogs ?? raw.TotalLogs ?? 0,
  lastActionAt: raw.lastActionAt ?? raw.LastActionAt ?? null,
  averageOpenDays: raw.averageOpenDays ?? raw.AverageOpenDays ?? 0,
});

const buildSearchPayload = (values) => {
  const payload = {
    operationModule: values.operationModule || "all",
  };
  if (values.fromDate) payload.fromDate = values.fromDate;
  if (values.toDate) payload.toDate = values.toDate;
  if (values.assignedToUserId !== "" && values.assignedToUserId != null) {
    payload.assignedToUserId = Number(values.assignedToUserId);
  }
  return payload;
};

const OperationsOwnerPerformanceReportPage = ({
  operationsSource = "Management",
  pageTitle = "گزارش عملکرد مسئولان عملیات",
}) => {
  const pageInfo = useGetPageInfo();
  const [filterLoading, setFilterLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filterValues, setFilterValues] = useState(initialFilters);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [rows, setRows] = useState([]);

  const headCells = useMemo(
    () => [
      { id: "assignedToFullName", label: "مسئول" },
      { id: "totalProcesses", label: "کل فرآیندها", type: "number" },
      { id: "openProcesses", label: "باز", type: "number" },
      { id: "completedProcesses", label: "تکمیل‌شده", type: "number" },
      {
        id: "overdueProcessesDisplay",
        label: "عقب‌افتاده",
        type: "chip",
        model: { colorField: "overdueChipColor" },
      },
      {
        id: "attentionProcessesDisplay",
        label: "نیازمند توجه",
        type: "chip",
        model: { colorField: "attentionChipColor" },
      },
      {
        id: "normalProcessesDisplay",
        label: "عادی",
        type: "chip",
        model: { colorField: "normalChipColor" },
      },
      { id: "totalLogs", label: "تعداد اقدامات", type: "number" },
      { id: "lastActionAtDisplay", label: "آخرین اقدام" },
      { id: "averageOpenDaysDisplay", label: "میانگین روزهای باز" },
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      rows.map((raw, index) => {
        const row = normalizeRow(raw);
        return {
          ...row,
          id: row.assignedToUserId ?? `unassigned-${index}`,
          overdueProcessesDisplay: String(row.overdueProcesses),
          overdueChipColor: row.overdueProcesses > 0 ? "error" : "default",
          attentionProcessesDisplay: String(row.attentionProcesses),
          attentionChipColor: row.attentionProcesses > 0 ? "warning" : "default",
          normalProcessesDisplay: String(row.normalProcesses),
          normalChipColor: row.normalProcesses > 0 ? "success" : "default",
          lastActionAtDisplay: row.lastActionAt
            ? toJalaliFullDate(row.lastActionAt)
            : "—",
          averageOpenDaysDisplay:
            row.openProcesses > 0 ? String(row.averageOpenDays) : "—",
        };
      }),
    [rows]
  );

  useEffect(() => {
    const loadFilters = async () => {
      try {
        setFilterLoading(true);
        const response = await OperationsOwnerPerformanceReportApi.getFilterData(
          operationsSource
        );
        if (response?.isSuccess) {
          setAssigneeOptions(
            response.data?.assignees || response.data?.Assignees || []
          );
        } else {
          toast.error(
            response?.message || "دسترسی به فیلترهای گزارش مجاز نیست"
          );
          setAssigneeOptions([]);
        }
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "خطا در دریافت فیلترهای گزارش";
        toast.error(msg);
        setAssigneeOptions([]);
      } finally {
        setFilterLoading(false);
      }
    };
    loadFilters();
  }, [operationsSource]);

  const onSearch = useCallback(async () => {
    try {
      setLoading(true);
      const response = await OperationsOwnerPerformanceReportApi.search(
        operationsSource,
        buildSearchPayload(filterValues)
      );
      if (response?.isSuccess) {
        setRows(response.data || []);
        if (!(response.data || []).length) {
          toast.info("داده‌ای برای نمایش یافت نشد");
        }
      } else {
        toast.error(response?.message || "خطا در دریافت گزارش");
        setRows([]);
      }
    } catch {
      toast.error("خطا در دریافت گزارش");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [operationsSource, filterValues]);

  const onClearFilters = () => {
    setFilterValues(initialFilters);
    setRows([]);
  };

  return (
    <Box>
      <BreadcrumbsComponent
        pageTitle={pageInfo?.title || pageTitle}
        pageDescription={pageInfo?.description}
      />

      <Card sx={{ mb: 2 }}>
        <CardHeader title="فیلترها" />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>نوع عملیات</InputLabel>
                <Select
                  label="نوع عملیات"
                  value={filterValues.operationModule}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      operationModule: e.target.value,
                    }))
                  }
                >
                  {OPERATION_MODULES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>مسئول فرآیند</InputLabel>
                <Select
                  label="مسئول فرآیند"
                  value={filterValues.assignedToUserId}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      assignedToUserId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">همه</MenuItem>
                  {assigneeOptions.map((item) => (
                    <MenuItem key={item.id} value={String(item.id)}>
                      {item.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="از تاریخ"
                fullWidth
                size="small"
                value={filterValues.fromDate}
                onChange={(value) =>
                  setFilterValues((prev) => ({
                    ...prev,
                    fromDate: value || "",
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="تا تاریخ"
                fullWidth
                size="small"
                value={filterValues.toDate}
                onChange={(value) =>
                  setFilterValues((prev) => ({
                    ...prev,
                    toDate: value || "",
                  }))
                }
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={onSearch}
              disabled={loading || filterLoading}
            >
              جستجو
              {loading && (
                <CircularProgress color="primary" size={16} sx={{ ml: 2 }} />
              )}
            </Button>
            <Button
              variant="text"
              color="inherit"
              startIcon={<HighlightOffIcon />}
              onClick={onClearFilters}
            >
              پاک‌سازی فیلترها
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="نتایج" />
        <Divider />
        <CardContent>
          <EnhancedTable
            headCells={headCells}
            rows={tableRows}
            loading={loading}
            emptyMessage="داده‌ای برای نمایش وجود ندارد"
          />
        </CardContent>
      </Card>
    </Box>
  );
};

OperationsOwnerPerformanceReportPage.propTypes = {
  operationsSource: PropTypes.oneOf(["Management", "SolarentOperator"]),
  pageTitle: PropTypes.string,
};

export default OperationsOwnerPerformanceReportPage;
